import { LoadingStatus, ProductDetailsSections, ProductPage } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { ProductDetailsContext, Tab, Tabs } from '@components';
import { useAppDispatch, useAppSelector, useCSSVar, useScrollPosition } from '@hooks';
import { Box, styled } from '@mui/material';
import {
  getDetailsSection,
  isInTransition,
  setDetailsSection,
  setInTransition,
} from '@store/product/product.slice';
import { t } from '@translations';
import { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';

// STYLES
//--------

const TabBox = styled(Box)`
  position: sticky;
  top: var(--top-bar-height);
  z-index: 1197;
  transition: all 200ms linear;
  &.stuck {
    box-shadow: none;
    border-radius: 0;
    .tabs {
      box-shadow: none;
      border-radius: 0;
    }
  }
`;

const FullWidthBar = styled(Box)`
  position: fixed;
  top: var(--top-bar-height);
  left: 10%;
  right: 10%;
  background: #fff;
  z-index: 1197;
  height: 50px;
  opacity: 0;
  transition: none;

  &.stuck {
    transition: all 200ms linear;
    left: 0;
    right: 0;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    opacity: 1;
  }
`;

// TYPES
//-------

export type ProductDetailsTabsProps = LoadingStatus & {
  product?: ProductPage;
};

type TabItem = {
  value: ProductDetailsSections;
  label: string;
};

// COMPONENTS
//------------
export const ProductDetailsTabs = ({ isLoading }: ProductDetailsTabsProps) => {
  const topBarHeight = useCSSVar('--top-bar-height');
  const parsedHeight = parseInt(topBarHeight, 10);
  const { sections = {} } = useContext(ProductDetailsContext);
  const dispatch = useAppDispatch();
  const currentSection = useAppSelector(getDetailsSection);
  const inTransition = useAppSelector(isInTransition);
  const setSection = (id: ProductDetailsSections) => dispatch(setDetailsSection(id));
  const tabConfig: TabItem[] = useMemo(() => {
    let tabs = [
      { value: 'description', label: t(TextCodes.Product.Details.Description) },
      { value: 'specifications', label: t(TextCodes.Product.Details.Specifications) },
      { value: 'documents', label: t(TextCodes.Product.Details.Documents) },
      { value: 'accessories', label: t(TextCodes.Product.Details.Accessories) },
      { value: 'similar', label: t(TextCodes.Product.Details.SimilarProducts) },
      { value: 'upsale', label: t(TextCodes.Product.Details.UpsaleProducts) },
      { value: 'related', label: t(TextCodes.Product.Details.RelatedMaterials) },
    ] as TabItem[];
    return tabs.filter((tab) => sections[tab.value]);
  }, [sections]);

  let transitionTimeout: any = null;

  const handleClick = (sectionId: string) => {
    if (inTransition) {
      return;
    }
    if (transitionTimeout) {
      clearTimeout(transitionTimeout);
    }
    setSection(sectionId as ProductDetailsSections);
    dispatch(setInTransition(true));
    const element = document.getElementById(sectionId);
    const top = (element?.offsetTop || 0) - parsedHeight;
    window?.scrollTo({ top, behavior: 'smooth' });
    transitionTimeout = setTimeout(() => dispatch(setInTransition(false)), 500);
  };
  if (tabConfig.length === 0) {
    return null;
  }

  const validCurrentSection = tabConfig.some((tab) => tab.value === currentSection)
    ? currentSection
    : tabConfig[0]?.value;

  return (
    <StickyNavBar>
      <TabBox>
        <Tabs
          variant='scrollable'
          scrollButtons='auto'
          value={validCurrentSection}
          className='tabs'
        >
          {tabConfig.map((tab) => (
            <Tab key={tab.value} {...tab} onClick={() => handleClick(tab.value)} />
          ))}
        </Tabs>
      </TabBox>
    </StickyNavBar>
  );
};

export type StickyNavBarProps = {
  children?: ReactNode;
  offset?: string;
};
export const StickyNavBar = ({ children }: StickyNavBarProps) => {
  const position = useScrollPosition();
  const ref = useRef<HTMLElement>();
  const topBarHeight = useCSSVar('--top-bar-height');
  const parsedHeight = parseInt(topBarHeight, 10);
  const [className, setClassName] = useState('');
  useEffect(() => {
    if (ref.current) {
      const top = ref.current.getBoundingClientRect().top;
      setClassName(top === parsedHeight ? 'stuck' : 'normal');
    }
  }, [position, parsedHeight]);
  return (
    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      <FullWidthBar className={`full-width-bar ${className}`} />
      <TabBox ref={ref} className={className}>
        {children}
      </TabBox>
    </Box>
  );
};

export type ProductDetailsTabsPlaceholderProps = LoadingStatus & {};

export const ProductDetailsTabsPlaceholder = () => {
  return <TabBox className='product-list'></TabBox>;
};
